// Pure, framework-free helpers for the org-wide "Require MFA" policy.
// The policy is stored as a JSON string in the `settings` table row named
// 'mfa_policy'. Enforcement only ever applies to users WITHOUT a verified
// factor — users who already have one are handled by the aal1->aal2 challenge.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const DEFAULT_MFA_POLICY = {
  enabled: false,
  scope: 'pilot', // 'pilot' = only pilotUserIds, 'all' = every staff member
  pilotUserIds: [],
  graceDays: 14,
  enforcedAt: null // ISO string set when `enabled` first flips true
};

/**
 * Parse the stored settings value into a normalised policy object with safe
 * defaults. Accepts a JSON string, an already-parsed object, or null.
 */
export const parsePolicy = value => {
  let raw = value;
  if (typeof value === 'string') {
    try {
      raw = JSON.parse(value);
    } catch {
      raw = null;
    }
  }
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_MFA_POLICY };
  return {
    enabled: !!raw.enabled,
    scope: raw.scope === 'all' ? 'all' : 'pilot',
    pilotUserIds: Array.isArray(raw.pilotUserIds) ? raw.pilotUserIds : [],
    graceDays:
      Number.isFinite(raw.graceDays) && raw.graceDays >= 0
        ? Math.floor(raw.graceDays)
        : DEFAULT_MFA_POLICY.graceDays,
    enforcedAt: raw.enforcedAt || null
  };
};

/** Whether the policy targets a given user profile id. */
export const isUserRequired = (policy, profileId) => {
  if (!policy?.enabled) return false;
  if (policy.scope === 'all') return true;
  return (policy.pilotUserIds || []).includes(profileId);
};

/** Grace deadline in epoch ms (Infinity when never enforced / no profile). */
const graceDeadline = policy => {
  if (!policy?.enforcedAt) return Infinity;
  const start = new Date(policy.enforcedAt).getTime();
  if (Number.isNaN(start)) return Infinity;
  return start + (policy.graceDays || 0) * MS_PER_DAY;
};

/**
 * Decide what enrolment gate (if any) a user is subject to. Enforcement only
 * ever impacts ACTIVE BOHSA admins — everyone else is left alone.
 * @returns {'off'|'grace'|'enroll'}
 *   'off'    — no action (not an active admin, not required, has a factor, or disabled)
 *   'grace'  — required but within the grace window: skippable nudge
 *   'enroll' — required and grace has elapsed: forced wall, no skip
 */
export const decideEnrolment = ({
  policy,
  profileId,
  isActiveAdmin,
  hasVerifiedFactor,
  now = Date.now()
}) => {
  if (hasVerifiedFactor) return 'off';
  if (!isActiveAdmin) return 'off';
  if (!isUserRequired(policy, profileId)) return 'off';
  return now >= graceDeadline(policy) ? 'enroll' : 'grace';
};

/** Whole days left in the grace window (>= 0), for display copy. */
export const graceDaysLeft = (policy, now = Date.now()) => {
  const deadline = graceDeadline(policy);
  if (!Number.isFinite(deadline)) return null;
  return Math.max(0, Math.ceil((deadline - now) / MS_PER_DAY));
};
